import { IRecord } from 'interfaces';
import React, { PropsWithChildren, createContext, useContext, useEffect } from 'react';

import { useAuthStore } from 'modules/auth/store/authStore';
import { useLogout } from 'modules/auth/store/authStore/hooks/useLogout';
import { getIsAnonymous } from 'modules/auth/store/authStore/selectors/getIsAnonymous';
import { logSegmentEvent } from 'utils/analytics';

import { useCleanUpListeningExperience } from '../hooks/useCleanUpStores';
import { GuestAccountBanner } from './banners/GuestAccountBanner';

export type SharedReadingExperienceProps = PropsWithChildren<{
  item: IRecord;
}>;

export const SharedExperienceContext = createContext<{
  item: IRecord;
} | null>(null);

export const SharedReadingExperience: React.FC<SharedReadingExperienceProps> = ({ children, item }) => {
  const logout = useLogout();
  useCleanUpListeningExperience();
  const user = useAuthStore(state => state.user);
  const isAnonymous = useAuthStore(getIsAnonymous);

  useEffect(() => {
    if (!user) return;

    // prep local speech synthesis (safari)
    window.speechSynthesis.cancel();

    logSegmentEvent('web_app_shared_document_opened', { fileType: item.recordType });
  }, [item.recordType, user]);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (isAnonymous) {
        logout();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isAnonymous, logout]);

  return <SharedExperienceContext.Provider value={{ item }}>{children}</SharedExperienceContext.Provider>;
};

export const useSignUpModal = () => {
  const sharedExperience = useContext(SharedExperienceContext);
  const isAnonymous = useAuthStore(getIsAnonymous);

  if (!sharedExperience) {
    return null;
  }

  const signUpBannerJsx = isAnonymous && <GuestAccountBanner />;

  return {
    signUpBannerJsx
  };
};
